// the channel's controls in the browser (decided Oct 26). ws4kp pauses for good on any skip, with nothing on screen
// to say so; here a skip holds the new screen for HOLD_MS with a countdown chip, then the loop carries on. Space (or a
// remote's play/pause) pauses with a PAUSED chip, lifted after PAUSE_MAX_MS. The two radars are one stop each, not
// nineteen frames. A summoned bar (any key, mouse move, tap or long press) carries back, play/pause, next, sound and
// the way out of full screen or the kiosk layout, which on an iPhone Home Screen app is the only way out.
// Interrupts break through any hold or pause (interrupt.mjs calls beginOverride/endOverride).
// Listeners are added at module load, before ws4kp's own (index.mjs adds its on DOMContentLoaded), so ours run first
// and stop ws4kp's from acting on the same key or swipe.
import {
	message, isPlaying, isLocationActive, currentDisplay, showDisplay, getDisplay,
} from '../navigation.mjs';
import STATUS from '../status.mjs';
import settings from '../settings.mjs';
import {
	isMuted, toggleMute, soundBlocked, onSoundBlocked,
} from './music.mjs';

const HOLD_MS = 30000;
const PAUSE_MAX_MS = 15 * 60000;
const BAR_MOUSE_MS = 3000;
const BAR_TOUCH_MS = 4000;
const LONG_PRESS_MS = 700;
const RADARS = new Set(['radar', 'future-radar']);

// 'play' | 'hold' | 'pause'
let state = 'play';
// whether the page has had its first tap, click or key, and whether the press being handled is that one
let touched = false;
let firstTouch = false;
let until = 0;
let override = null;
let barTimer = null;
let chip = null;
let bar = null;

const nav = (command) => message({ type: 'navButton', message: command });

// the bar's icons as inline SVG in the plate's gold (the Star4000 fonts have no music note or media glyphs)
const svg = (body) => `<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" fill="currentColor">${body}</svg>`;
const ICON = {
	prev: svg('<path d="M6 5h2.5v14H6zM20 5v14L9.5 12z"/>'),
	next: svg('<path d="M15.5 5H18v14h-2.5zM4 5v14l10.5-7z"/>'),
	play: svg('<path d="M7 4.5v15L19.5 12z"/>'),
	pause: svg('<path d="M6.5 5h4v14h-4zM13.5 5h4v14h-4z"/>'),
	sound: svg('<path d="M4 9h4l5-4.5v15L8 15H4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M17.8 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'),
	muted: svg('<path d="M4 9h4l5-4.5v15L8 15H4z"/><path d="M16 9l5 6M21 9l-5 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>'),
	full: svg('<path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" fill="none" stroke="currentColor" stroke-width="2.4"/>'),
	shrink: svg('<path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" fill="none" stroke="currentColor" stroke-width="2.4"/>'),
};
const remaining = () => Math.max(0, until - Date.now());

// screens a person can land on: loaded, with something to show, not a clock feature or the interrupt
const landable = (display) => display && display.status === STATUS.loaded && display.timing?.totalScreens > 0
	&& !display.isFeature && display.elemId !== 'progress';
const neighbour = (from, step) => {
	for (let i = 1; i < 64; i += 1) {
		const display = getDisplay((((from?.navId ?? 0) + step * i) % 64 + 64) % 64);
		if (landable(display)) return display;
	}
	return null;
};

// ---------- the chip and the bar ----------

const ensureUi = () => {
	if (bar) return;
	const twc = document.querySelector('#divTwc');
	const container = document.querySelector('#container');
	if (!twc || !container) return;
	chip = document.createElement('div');
	chip.className = 'rhl-chip';
	chip.hidden = true;
	container.append(chip);
	bar = document.createElement('div');
	bar.className = 'rhl-bar';
	bar.setAttribute('role', 'toolbar');
	bar.setAttribute('aria-label', 'Channel controls');
	bar.innerHTML = `
		<button type="button" data-act="prev" aria-label="Back">${ICON.prev}</button>
		<button type="button" data-act="toggle" aria-label="Pause">${ICON.pause}</button>
		<button type="button" data-act="next" aria-label="Next">${ICON.next}</button>
		<button type="button" data-act="sound" aria-label="Sound off">${ICON.sound}</button>
		<button type="button" data-act="screen" class="rhl-bar-exit" aria-label="Full size">${ICON.full}</button>`;
	// the bar's own presses never reach the screen beneath (no swipe, no tap-to-hide)
	['pointerdown', 'touchstart', 'touchend', 'click'].forEach((type) => bar.addEventListener(type, (e) => e.stopPropagation()));
	bar.addEventListener('click', (e) => {
		const button = e.target.closest('button');
		if (!button) return;
		e.preventDefault();
		// eslint-disable-next-line no-use-before-define
		act(button.dataset.act);
		button.blur();
	});
	twc.append(bar);
};

// the page's three sizes (Oct 26): kiosk in the browser window (how it loads), full size (the screen), and ws4kp's
// own layout with all its controls (kiosk=false, for the control room). The bar's screen button goes between the
// first two only; from ws4kp's layout it goes back to kiosk. Phones without element full screen (iPhone) get none.
const canFullScreen = () => !!document.fullscreenEnabled && !!(document.querySelector('#divTwc') ?? document.documentElement).requestFullscreen;
// an iPhone has no full screen for pages in any browser (they all run on Safari's engine); a page added to the Home
// Screen opens with no browser bars, so there the button explains how (Oct 26)
const standalone = () => window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;
const homeScreenHint = () => !canFullScreen() && !standalone();
// the how-to card (Oct 26): what the full-size button opens where pages can't go full screen. It names the device
// and the browser, says why, and walks through every tap to the Home Screen, where the channel opens with no browser
// bars. Sized to the phone's own screen (not the channel's frame), so it reads in portrait too.
const HOWTO_MS = 45000;
const ua = navigator.userAgent;
const device = () => {
	if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'iPad';
	if (/iPhone|iPod/.test(ua)) return 'iPhone';
	return 'iPhone or iPad';
};
const browser = () => {
	if (/CriOS/.test(ua)) return 'Chrome';
	if (/FxiOS/.test(ua)) return 'Firefox';
	if (/EdgiOS/.test(ua)) return 'Edge';
	if (/DuckDuckGo/.test(ua)) return 'DuckDuckGo';
	if (/Safari/.test(ua)) return 'Safari';
	return null;
};
const glyph = (body) => `<svg viewBox="0 0 24 24" aria-hidden="true">${body}</svg>`;
const G = {
	share: glyph('<path d="M12 3v12M7.5 7.5 12 3l4.5 4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M8 10H6a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1h-2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'),
	more: glyph('<circle cx="5" cy="12" r="2" fill="currentColor"/><circle cx="12" cy="12" r="2" fill="currentColor"/><circle cx="19" cy="12" r="2" fill="currentColor"/>'),
	list: glyph('<path d="M8 6h12M8 12h12M8 18h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="4" cy="6" r="1.4" fill="currentColor"/><circle cx="4" cy="12" r="1.4" fill="currentColor"/><circle cx="4" cy="18" r="1.4" fill="currentColor"/>'),
	add: glyph('<rect x="4" y="4" width="16" height="16" rx="4" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 8v8M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'),
	done: glyph('<path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>'),
	home: glyph('<rect x="3" y="3" width="7" height="7" rx="2" fill="currentColor"/><rect x="14" y="3" width="7" height="7" rx="2" fill="currentColor" opacity=".5"/><rect x="3" y="14" width="7" height="7" rx="2" fill="currentColor" opacity=".5"/><rect x="14" y="14" width="7" height="7" rx="2" fill="currentColor" opacity=".5"/>'),
};
// every tap, for the browser in hand; iOS 26 Safari keeps Share behind the ••• button in its default layout, and the
// share sheet may hide Add to Home Screen until View More
const steps = (name, where) => {
	const first = {
		Safari: [G.more, '<b>&bull;&bull;&bull;</b> next to the address bar <i>(skip if you see Share)</i>'],
		Chrome: [G.share, '<b>Share</b>, in the address bar at the top'],
	}[name] ?? [G.more, `your browser's menu, then <b>Share</b>`];
	return [
		first,
		...(name === 'Safari' ? [[G.share, '<b>Share</b>']] : []),
		[G.list, '<b>View More</b> <i>(if Add to Home Screen isn&rsquo;t listed)</i>'],
		[G.add, '<b>Add to Home Screen</b>'],
		[G.done, '<b>Add</b>, top right'],
	];
};
let howto = null;
let howtoTimer = null;
const hideHowto = () => {
	clearTimeout(howtoTimer);
	if (!howto || howto.hidden) return;
	howto.classList.add('gone');
	setTimeout(() => { if (howto.classList.contains('gone')) howto.hidden = true; }, 300);
};
const showHint = () => {
	if (!howto) {
		howto = document.createElement('div');
		howto.className = 'rhl-howto';
		howto.setAttribute('role', 'dialog');
		howto.setAttribute('aria-label', 'Full screen');
		// a tap anywhere on it closes it; it never reaches the screen beneath
		['pointerdown', 'touchstart', 'touchend', 'click'].forEach((type) => howto.addEventListener(type, (e) => e.stopPropagation()));
		howto.addEventListener('click', hideHowto);
		// inside #divTwc: kiosk mode hides everything else in the body
		(document.querySelector('#divTwc') ?? document.body).append(howto);
	}
	const where = device();
	const name = browser();
	howto.innerHTML = `<div class="card">
		<div class="hd">Full screen on your ${where}</div>
		<p>${where === 'iPhone or iPad' ? 'iPhones and iPads don&rsquo;t' : `An ${where} doesn&rsquo;t`} let web pages go full screen${name ? `, in ${name} or any other browser` : ''}. Add Russian Hill Local to your Home Screen instead: it opens there with no browser bars.</p>
		<ol>${steps(name, where).map(([icon, text]) => `<li><span class="ic">${icon}</span><span>Tap ${text}</span></li>`).join('')}</ol>
		<div class="ft"><span class="ic">${G.home}</span><span>Then open <b>RH Local</b> from your Home Screen.</span></div>
		<div class="ok">Tap to close</div>
	</div>`;
	howto.hidden = false;
	howto.classList.remove('gone');
	clearTimeout(howtoTimer);
	howtoTimer = setTimeout(hideHowto, HOWTO_MS);
};

// sets markup only when it differs, so a repaint doesn't touch the page for nothing
// (icons are tracked by name: the browser rewrites SVG markup, so comparing innerHTML would never match)
const setIcon = (el, name) => {
	if (el.dataset.icon === name) return;
	el.dataset.icon = name;
	el.innerHTML = ICON[name];
};
const setAttr = (el, name, value) => { if (el.getAttribute(name) !== value) el.setAttribute(name, value); };

const paint = () => {
	ensureUi();
	if (!bar) return;
	const toggle = bar.querySelector('[data-act="toggle"]');
	const stopped = state !== 'play';
	setIcon(toggle, stopped ? 'play' : 'pause');
	setAttr(toggle, 'aria-label', stopped ? 'Play' : 'Pause');
	const sound = bar.querySelector('[data-act="sound"]');
	// until the first tap or click the button shows muted wherever the browser holds sound back; that first touch
	// (anywhere, the button included) flips it to sound on at once, not when the music gets round to starting (Oct 26)
	const waiting = soundBlocked() && !touched;
	const silent = isMuted() || waiting;
	sound.classList.toggle('off', silent);
	setIcon(sound, silent ? 'muted' : 'sound');
	setAttr(sound, 'aria-label', silent ? 'Sound on' : 'Sound off');
	sound.classList.toggle('blocked', waiting && !isMuted());
	const screen = bar.querySelector('[data-act="screen"]');
	const full = !!document.fullscreenElement;
	const kiosk = !!settings.kiosk?.value;
	screen.hidden = kiosk && !full && !canFullScreen() && !homeScreenHint();
	setIcon(screen, full || !kiosk ? 'shrink' : 'full');
	setAttr(screen, 'aria-label', full ? 'Back to the window' : kiosk ? 'Full size' : 'Kiosk view');
	if (state === 'hold') {
		chip.hidden = false;
		chip.className = 'rhl-chip';
		chip.textContent = `HOLD 0:${String(Math.ceil(remaining() / 1000)).padStart(2, '0')}`;
	} else if (state === 'pause') {
		chip.hidden = false;
		chip.className = 'rhl-chip paused';
		chip.textContent = 'PAUSED';
	} else {
		chip.hidden = true;
	}
	if (state === 'pause') bar.classList.add('shown');
};

const showBar = (ms) => {
	ensureUi();
	if (!bar) return;
	bar.classList.add('shown');
	document.querySelector('#divTwc')?.classList.remove('no-cursor');
	clearTimeout(barTimer);
	barTimer = setTimeout(() => {
		if (state === 'pause') return;
		bar.classList.remove('shown');
		if (document.fullscreenElement || settings.kiosk?.value) document.querySelector('#divTwc')?.classList.add('no-cursor');
	}, ms);
	paint();
};
const hideBar = () => {
	clearTimeout(barTimer);
	if (state !== 'pause') bar?.classList.remove('shown');
};

// ---------- state ----------

const setState = (next, ms = 0) => {
	state = next;
	until = ms ? Date.now() + ms : 0;
	paint();
};

const resume = () => {
	if (override) return;
	setState('play');
	nav('play');
};

const hold = () => setState('hold', HOLD_MS);

// ---------- the trail: back returns to what was on before (Oct 27) ----------
// Going forward, the rotation picks the next screen (navigation.mjs patch 7), so ws4kp's "previous" (the screen before
// in nav-id order) no longer undoes a step: back then forward landed somewhere new each time. Every change of screen,
// skipped or aired, now leaves a trail entry (the screen and the page it was on); back at a screen's first page walks
// the trail, and forward after a back retraces it, as a browser does. The Close-Up and News Desk page within themselves.
const PAGED_FEATURES = new Set(['market-closeup', 'news-desk']);
const TRAIL_MAX = 24;
const trail = [];
const ahead = [];
let seen = { display: null, index: 0 };
const trailable = (display) => display && display.elemId !== 'progress' && display.elemId !== 'interrupt';
const pagedHere = (display) => display && !RADARS.has(display.elemId) && (!display.isFeature || PAGED_FEATURES.has(display.elemId));

// note what's on; a change of screen we didn't make by retracing puts the last one on the trail and forgets the way ahead
const note = () => {
	const current = currentDisplay() ?? null;
	if (current !== seen.display) {
		if (trailable(seen.display)) {
			trail.push(seen);
			if (trail.length > TRAIL_MAX) trail.shift();
		}
		ahead.length = 0;
	}
	seen = { display: current, index: Math.max(0, current?.screenIndex ?? 0) };
};

// a page of the screen on now, stopped there
const goPage = (display, index) => {
	nav('stop');
	display.navBaseCount = index <= 0 ? 0 : display.timing.fullDelay[index - 1];
	display.updateScreenFromBaseCount();
};

// put a trail entry back on screen, at its page; false if it can't be shown any more
const restore = (entry) => {
	const current = currentDisplay();
	if (!trailable(entry.display) || entry.display.status !== STATUS.loaded) return false;
	nav('stop');
	if (current?.elemId === 'interrupt') override = null;
	if (current?.isFeature && current !== entry.display) current.disarm();
	showDisplay(entry.display);
	if (currentDisplay() !== entry.display) return false;
	const pages = entry.display.timing?.totalScreens ?? 0;
	const index = pagedHere(entry.display) ? Math.min(entry.index, pages - 1) : 0;
	if (index > 0) goPage(entry.display, index);
	// a radar comes back from its first frame
	seen = { display: entry.display, index: Math.max(0, entry.display.screenIndex) };
	return true;
};

// walk one stack (trail or ahead) back onto the screen, keeping what's on now on the other one
const retrace = (from, to) => {
	const leaving = { display: currentDisplay(), index: Math.max(0, currentDisplay()?.screenIndex ?? 0) };
	while (from.length) {
		if (restore(from.pop())) {
			if (trailable(leaving.display)) to.push(leaving);
			return true;
		}
	}
	return false;
};

// a skip: one page on ordinary screens and the Close-Up and News Desk; the whole screen on the radars and the camera
// features. Across screens, back retraces the trail and forward retraces a back. Then hold.
const skip = (dir) => {
	if (!isLocationActive()) return;
	note();
	const current = currentDisplay();
	const pages = current?.timing?.totalScreens ?? 0;
	const index = Math.max(0, current?.screenIndex ?? 0);
	if (pagedHere(current) && dir < 0 && index > 0) {
		// straight to the page before (ws4kp's previous restarts the page when it's partway through)
		goPage(current, index - 1);
		note();
		hold();
		return;
	}
	if (pagedHere(current) && dir > 0 && index < pages - 1) {
		goPage(current, index + 1);
		note();
		hold();
		return;
	}
	if (dir < 0 ? retrace(trail, ahead) : retrace(ahead, trail)) {
		hold();
		return;
	}
	if (current && (RADARS.has(current.elemId) || current.isFeature)) {
		// forward goes where the loop would: the rotation's next pick (nav-id order only if it has none)
		const picked = dir > 0 ? window.RHL?.nextScreen?.() : null;
		const target = picked && picked !== current ? picked : neighbour(current, dir);
		nav('stop');
		// skipping away from an interrupt ends it: its override is over, and this skip's hold takes over
		if (current.elemId === 'interrupt') override = null;
		if (current.isFeature) current.disarm();
		if (target) showDisplay(target);
	} else {
		nav(dir > 0 ? 'next' : 'previous');
		// landing on a radar from the next screen: start it from its first frame
		const landed = currentDisplay();
		if (dir < 0 && landed && RADARS.has(landed.elemId)) showDisplay(landed);
	}
	note();
	hold();
};

const togglePause = () => {
	if (!isLocationActive()) return;
	if (state === 'play') {
		nav('stop');
		setState('pause', PAUSE_MAX_MS);
	} else {
		resume();
	}
};

// kiosk in the window <-> full size; from ws4kp's own layout, back to kiosk
const toggleScreen = () => {
	if (document.fullscreenElement) {
		document.exitFullscreen?.().catch(() => {});
	} else if (!settings.kiosk?.value) {
		settings.kiosk.value = true;
	} else if (canFullScreen()) {
		// the channel's own box goes full screen, as ws4kp's button and F key do: ws4kp's scaling and its
		// #divTwc:fullscreen styles assume that element. Full-screening the whole page (as before Oct 26) left the
		// kiosk layout in place under the fullscreen scaling, which framed the wrong part of the page. The bar, chips
		// and prompts all live inside #divTwc, so they come along.
		(document.querySelector('#divTwc') ?? document.documentElement).requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
	} else if (homeScreenHint()) {
		showHint();
	}
	setTimeout(paint, 300);
};

const act = (name) => {
	if (name === 'prev') skip(-1);
	else if (name === 'next') skip(1);
	else if (name === 'toggle') togglePause();
	// the press that is the page's first touch only starts sound (the music player's own listener sees the same click);
	// toggling as well would mute it straight away
	else if (name === 'sound') { if (!firstTouch) toggleMute(); }
	else if (name === 'screen') toggleScreen();
	showBar(BAR_MOUSE_MS);
	firstTouch = false;
};

// every second: count the hold or pause down, and lift it when it runs out
setInterval(() => {
	note();
	if (state !== 'play' && !override && remaining() === 0) resume();
	else if (state === 'hold') paint();
}, 250);

// ---------- interrupts ----------

// an interrupt is about to air: play, whatever the state, and remember what to go back to
const beginOverride = () => {
	if (state === 'play' || override) return;
	override = { state, left: remaining() };
	if (!isPlaying()) nav('play');
};
// it has finished: back to the hold or pause, with the time it had left
const endOverride = () => {
	if (!override) return;
	const { state: was, left } = override;
	override = null;
	if (left <= 0) { resume(); return; }
	nav('stop');
	setState(was, left);
};

// ---------- input ----------

const typing = (target) => target?.closest?.('input, textarea, select, [contenteditable="true"]');

document.addEventListener('keydown', (e) => {
	if (typing(e.target) || e.ctrlKey || e.altKey || e.metaKey) return;
	const key = e.key;
	const handled = {
		ArrowRight: () => skip(1),
		PageDown: () => skip(1),
		MediaTrackNext: () => skip(1),
		MediaFastForward: () => skip(1),
		ArrowLeft: () => skip(-1),
		PageUp: () => skip(-1),
		MediaTrackPrevious: () => skip(-1),
		MediaRewind: () => skip(-1),
		' ': () => togglePause(),
		MediaPlayPause: () => togglePause(),
	}[key];
	// any key shows the bar; the ones above also act, and ws4kp's own handler doesn't see them
	showBar(BAR_MOUSE_MS);
	if (!handled || e.shiftKey) return;
	if (!(document.fullscreenElement || document.activeElement === document.body || document.activeElement === null
		|| document.querySelector('#divTwc')?.contains(document.activeElement))) return;
	e.preventDefault();
	e.stopImmediatePropagation();
	handled();
});

const container = document.querySelector('#container');
['swiped-left', 'swiped-right'].forEach((type) => container?.addEventListener(type, (e) => {
	e.stopImmediatePropagation();
	skip(type === 'swiped-left' ? 1 : -1);
	showBar(BAR_TOUCH_MS);
}));

const twc = document.querySelector('#divTwc');
// the mouse summons the bar only on a deliberate move (about 30 px within half a second), not a nudge of the desk.
// Distance comes from the pointer's own positions: Chrome reports the first move after a page loads as a jump from
// the screen's corner, so movementX can't be trusted for it
let travel = 0;
let travelAt = 0;
let last = null;
// pointermove from a real mouse only: a tap's emulated mouse moves would otherwise redraw the bar during the tap's
// hover step, which Safari's engine takes as a hover and answers with no click
twc?.addEventListener('pointermove', (e) => {
	if (e.pointerType !== 'mouse') return;
	const now = Date.now();
	if (now - travelAt > 500) travel = 0;
	travelAt = now;
	if (last) travel += Math.abs(e.clientX - last.x) + Math.abs(e.clientY - last.y);
	last = { x: e.clientX, y: e.clientY };
	if (travel >= 30 || bar?.classList.contains('shown')) {
		travel = 0;
		showBar(BAR_MOUSE_MS);
	}
});
twc?.addEventListener('pointerleave', () => { last = null; });

// a tap shows the bar, or hides it if it's up (a tap never toggles play); a long press shows it for longer
let press = null;
twc?.addEventListener('touchstart', (e) => {
	if (e.touches.length !== 1) return;
	const t = e.touches[0];
	press = { x: t.clientX, y: t.clientY, at: Date.now(), long: setTimeout(() => { press = null; showBar(10000); }, LONG_PRESS_MS) };
}, { passive: true });
twc?.addEventListener('touchmove', (e) => {
	if (!press) return;
	const t = e.touches[0];
	if (Math.abs(t.clientX - press.x) > 12 || Math.abs(t.clientY - press.y) > 12) { clearTimeout(press.long); press = null; }
}, { passive: true });
twc?.addEventListener('touchend', () => {
	if (!press) return;
	clearTimeout(press.long);
	press = null;
	if (bar?.classList.contains('shown') && state !== 'pause') hideBar();
	else showBar(BAR_TOUCH_MS);
});
twc?.addEventListener('click', (e) => {
	if (e.detail === 0 || e.target.closest?.('.rhl-bar')) return;
	if (!('ontouchstart' in window)) showBar(BAR_MOUSE_MS);
});

document.addEventListener('fullscreenchange', () => paint());

// the sound prompt (Oct 26): when the browser holds sound back, a small plate over the crawl says how to start it,
// worded for the device; it goes the moment sound starts, or fades on its own after PROMPT_MS. It sits inside the
// frame, so it scales with the channel on a phone, a laptop or a TV, and steps aside while the bar is up
const PROMPT_MS = 6000;
let prompt = null;
let promptTimer = null;
const hidePrompt = () => {
	clearTimeout(promptTimer);
	if (!prompt) return;
	prompt.classList.add('gone');
	setTimeout(() => { if (prompt?.classList.contains('gone')) prompt.hidden = true; }, 600);
};
const showPrompt = () => {
	if (!prompt) {
		prompt = document.createElement('div');
		prompt.className = 'rhl-sound-prompt';
		prompt.setAttribute('role', 'status');
		document.querySelector('#container')?.append(prompt);
	}
	const touch = window.matchMedia?.('(pointer: coarse)').matches;
	prompt.innerHTML = `${ICON.muted}<span>${touch ? 'Tap for sound' : 'Click for sound'}</span>`;
	prompt.hidden = false;
	prompt.classList.remove('gone');
	clearTimeout(promptTimer);
	promptTimer = setTimeout(hidePrompt, PROMPT_MS);
};
// the first tap, click or key on the page: the browser lets sound start from here, so the button and prompt say so now
const firstInteraction = () => {
	if (touched) return;
	touched = true;
	firstTouch = true;
	['pointerdown', 'touchend', 'click', 'keydown'].forEach((type) => document.removeEventListener(type, firstInteraction, true));
	hidePrompt();
	paint();
	// a press that wasn't on the bar's sound button leaves nothing for act() to clear
	setTimeout(() => { firstTouch = false; }, 400);
};
['pointerdown', 'touchend', 'click', 'keydown'].forEach((type) => document.addEventListener(type, firstInteraction, true));

onSoundBlocked((blocked) => {
	if (blocked) showPrompt();
	else hidePrompt();
	paint();
});

export {
	beginOverride,
	endOverride,
	skip,
	togglePause,
};
